import { Injectable } from '@angular/core';
import { AppNavV2 } from 'src/app/shared/models/app-nav-v2.model';
import { ProductAppCodes, ProductAppImagePrefix } from '../product-info/product-info.service';
import { AppNavV2ItemConfigurationService } from './app-nav-v2-item-configuration.service';
import { AppNavV2ReportService } from './app-nav-v2-report.service';
import { Future } from 'src/app/util/future';
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service';

@Injectable({
  providedIn: 'root'
})
export class AppNavV2HelperService {
  public appNavV2: AppNavV2[] = [];
  private _isInitialized: Future<boolean> = new Future<boolean>();

  constructor
    (
      private _appNavV2ItemConfigurationService: AppNavV2ItemConfigurationService,
      private _appNavV2ReportService: AppNavV2ReportService,
      private _featureToggleService: FeatureToggleService
    ) { }

  public initialize(appNavV2Data: AppNavV2[]) {
    this.appNavV2 = appNavV2Data;
    this.addFreckleAndLaliloReports();
    this._isInitialized.resolve(true);
  }

  public getAvailableV2SearchReportItems(searchReportItems: AppNavV2[]) {
    let items = this._appNavV2ReportService.addImagePrefixes(searchReportItems);

    if (this.isLicensedAppCode(ProductAppCodes.Freckle) == true) {
      let freckleItems = this._appNavV2ItemConfigurationService.getFreckleSearchReports();
      items.push(...freckleItems);
    }
    if (this.isLicensedAppCode(ProductAppCodes.Lalilo) == true) {
      let laliloItems = this._appNavV2ItemConfigurationService.getLaliloSearchReports();
      items.push(...laliloItems);
    }
    return items;
  }

  public getMyDataNavsV2(reportParent: AppNavV2) {
    this.setImagePrefix([reportParent], ProductAppCodes.Reports, ProductAppImagePrefix.Reports);
    reportParent.children = this.configureReports(reportParent.children || []);

    return reportParent;
  }

  private configureReports(myReports: AppNavV2[]) {
    return this._appNavV2ReportService.addImagePrefixes(myReports);
  }

  private addFreckleAndLaliloReports() {
    let taggedReport = this.getAppNavDataByTag("report");
    if (taggedReport.length == 0) {
      return;
    }

    let reportsParent = taggedReport[0];

    let reports = reportsParent.children || [];

    if (this.isLicensedAppCode(ProductAppCodes.Freckle)) {
      reports.push(this._appNavV2ItemConfigurationService.getFreckleReports());
    }

    if (this.isLicensedAppCode(ProductAppCodes.Lalilo)) {
      reports.push(this._appNavV2ItemConfigurationService.getLaliloReports());
    }

    this._appNavV2ReportService.sortReports(reports);
  }

  public async getMyAppNavsV2(appNavV2: AppNavV2[]): Promise<AppNavV2[]> {
    this.setImagePrefix(appNavV2, ProductAppCodes.AR, ProductAppImagePrefix.AR);
    this.setImagePrefix(appNavV2, ProductAppCodes.Dna, ProductAppImagePrefix.Dna);
    this.setImagePrefix(appNavV2, ProductAppCodes.FastBridge, ProductAppImagePrefix.Fastbridge);
    this.setImagePrefix(appNavV2, ProductAppCodes.Freckle, ProductAppImagePrefix.Freckle);
    this.setImagePrefix(appNavV2, ProductAppCodes.Lalilo, ProductAppImagePrefix.Lalilo);
    this.setImagePrefix(appNavV2, ProductAppCodes.Myon, ProductAppImagePrefix.Myon);
    this.setImagePrefix(appNavV2, ProductAppCodes.Schoolzilla, ProductAppImagePrefix.Schoolzilla);
    this.setImagePrefix(appNavV2, ProductAppCodes.RENU, ProductAppImagePrefix.RENU);
    this.setImagePrefix(appNavV2, ProductAppCodes.Star, ProductAppImagePrefix.Star);
    this.setImagePrefix(appNavV2, ProductAppCodes.Nearpod, ProductAppImagePrefix.Nearpod);
    this.setImagePrefix(appNavV2, ProductAppCodes.RENED, ProductAppImagePrefix.RENED);

    let nearpodApp: AppNavV2 = {
      appCode: ProductAppCodes.Nearpod,
      displayName: 'Nearpod',
      imagePrefix: 'nearpod_logo',
      url: 'https://nearpod.com/?referrer=ren_next',
    };

    if (await this._featureToggleService.isTrueAsync('use-next-nearpod-url')) {
      nearpodApp.url = 'https://nearpod.com/renaissance-next?referrer=ren_next';
    };

    appNavV2 = this.insertInAlphabeticalOrderButBeforeEducatorAcademy(appNavV2, nearpodApp);

    return appNavV2;
  }

  public getAppNavDataByTag(tagName: string): AppNavV2[] {
    return this.appNavV2.filter(nav => nav.tags?.find(t => t == tagName));
  }

  public getApps(): AppNavV2[] {
    return this.appNavV2.filter(nav => !nav.tags?.includes('hidden') && !nav.tags?.includes('report') && !nav.tags?.includes('searchreport'));
  }

  public isLicensedAppCode(appCode: string): boolean {
    let item = this.appNavV2.find(item => item.appCode == appCode);
    return item != null ? true : false
  }

  public isInitialized(): Promise<boolean> {
    return this._isInitialized.promise;
  }

  private setImagePrefix(appNavV2: AppNavV2[], appCode: string, imagePrefix: string) {
    let item = appNavV2.find(code => code.appCode == appCode);
    if (item != null) {
      item.imagePrefix = imagePrefix;
    }
  }

  private insertInAlphabeticalOrderButBeforeEducatorAcademy(appNav: AppNavV2[], newItem: AppNavV2) {
    let index = 0;
    while (index < appNav.length && newItem.displayName.localeCompare(appNav[index].displayName) > 0 && appNav[index].appCode !== ProductAppCodes.RENED) {
      index++;
    }
    appNav.splice(index, 0, newItem);
    return appNav;
  }
}
